/**
 * dateUtils.js
 * 
 * Centralized utility for parsing and formatting dates across the MyEMR system.
 * Designed to handle Greek formats (dd/mm/yyyy) and standard ISO strings.
 */

/**
 * Robustly parses a date value into a Date object.
 * Handles:
 * - Date objects
 * - ISO strings (yyyy-mm-dd...)
 * - Greek strings (dd/mm/yyyy, dd-mm-yyyy)
 * - Numeric timestamps
 */
export const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // 1. Try standard ISO parsing first
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) return date;

    // 2. Handle dd/mm/yyyy or dd-mm-yyyy formats
    const greekRegex = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
    const match = trimmed.match(greekRegex);
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // 0-indexed
      const year = parseInt(match[3], 10);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) return d;
    }
  }

  if (typeof value === 'number') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }

  return null;
};

/**
 * Formats a Date value (string, Date, or number) to Greek dd/mm/yyyy format.
 */
export const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleDateString('el-GR');
};

/**
 * Computes age from a birth date.
 */
export const computeAge = (birthDateValue) => {
  const birthDate = parseDate(birthDateValue);
  if (!birthDate) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

/**
 * Returns a string formatted for HTML5 date inputs (yyyy-mm-dd).
 */
export const formatForInput = (value) => {
  const date = parseDate(value);
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
